import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {Store} from '@ngxs/store';
import {
  AuctionItem,
  AuctionService,
  BuyVdpState,
  CalculateBookValuesRequest,
  CalculatedBookValuesDto, ConfigurationState,
  Fee,
  FeeType,
  GetInventoryItem,
  InventoryItem,
  InventoryService,
  LaunchDarklyService,
  OrganizationFees,
  OrganizationState,
  PostingDataResponseModel,
  VehiclePostingConfigDto,
  EditPostingState,
  SetBookValueRecalculationNotNeeded,
  SetDisplayedBookValues,
  UpdatePosting,
  BookValueSpreadProviderName,
  EditPostingFormNames,
  FeeCodes,
  FeeTypes,
  PostingTypes,
  BookProviderService
} from 'remarketing-angular-library';
import {cloneDeep, find as _find, get as _get, set as _set} from 'lodash';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import {formatCurrency} from '../../../../../app/shared/utils/util';
import {delay, distinctUntilChanged, map, take, takeUntil} from 'rxjs/operators';
import {BookValueModalComponent} from '../../../../../vehicles/components/book-value-modal/book-value-modal.component';
import {ActivatedRoute, Router} from '@angular/router';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import {openingPriceCustomValidator} from './pricing-and-fees-form.validator';
import {PostingFormDirective} from '../posting-form/posting-form.directive';
import {
  BookValueTabsBlackBook, BookValueTabsComponent,
  BookValueTabsManheim,
  BookValueTabsNADA,
  CharacterCountComponent, CharacterCountDirective, SpinnerComponent
} from 'remarketing-ui-components-lib';
import {
  BookValueHelperService,
  OrganizedBookValue
} from '../../../../../vehicles/services/book-value-helper/book-value-helper.service';
import {PostingFormTrackingService} from '../../services/posting-form-tracking.service';
import {PostingAnalyticsHelperService} from '../../services/posting-analytics-helper.service';
import { CurrencyFormatService } from 'smartauction-frontend-micro-library';
import { EditPostingUtilsService } from '../../services/edit-posting-utils.service';
import { NgxMaskModule } from 'ngx-mask';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'app-pricing-and-fees-form',
    templateUrl: './pricing-and-fees-form.component.html',
    styleUrls: ['./pricing-and-fees-form.component.scss'],
    imports: [
        TranslocoDirective,
        ReactiveFormsModule,
        CharacterCountComponent,
        CharacterCountDirective,
        NgxMaskModule,
        SpinnerComponent,
        BookValueTabsComponent,
        BookValueModalComponent,
        CommonModule
    ]
})
export class PricingAndFeesFormComponent extends PostingFormDirective implements OnInit {
  @ViewChild(BookValueModalComponent) bookValuesModalComponent: BookValueModalComponent;
  postingData: PostingDataResponseModel;
  priceFeesForm: UntypedFormGroup;
  remainingDaysForm: UntypedFormGroup;
  saleFormattedFee: string;
  postingFormattedFee: string;
  auctionCmtFormattedFee: string;
  remainingDays: number;
  hitCount: number;
  defaultOpeningPrice = 0;
  defaultFormattedOpeningPrice = '$0';
  regPatternForMultiples = '^[1-9]+[0-9]*00$|^0$';
  vehiclePostingConfig: VehiclePostingConfigDto[];
  showLoader: boolean;
  postingType: PostingTypes;
  EditPostingFormNames = EditPostingFormNames;

  recalculatedOrganizedModalData: Array<OrganizedBookValue>;

  auctionItem: AuctionItem;
  vehicleOwnerCode: string;
  inventoryItem: InventoryItem;
  bookValueRecalculationInProgress = false;
  blackBookValues: BookValueTabsBlackBook;
  nadaValues: BookValueTabsNADA;
  manheimValues: BookValueTabsManheim;
  bookValue = 0;
  @Input() postingTypeString: string;

  public readonly errorMsg = {
    multiplesPatternErr: '',
    openingPrice: {
      reqErr: '',
      defaultOpeningPriceErr: ''
    },
    buyNowPrice: {
      exceedsOpeningPriceErr: ''
    }
  };
  bookValueTrans: any;
  characterCountData: any;

  formattedBlackBookValues = {
    wholesaleAverage: '',
    wholesaleClean: '',
    wholesaleRough: '',
    financeAdvance: '',
    averageTradeIn: ''
  };

  formattedNadaValues = {
    tradeInClean: '',
    tradeInAverage: '',
    tradeInRough: '',
    retailClean: '',
    loanClean: ''
  };

  formattedManheimValues = {
    above: '',
    average: '',
    below: ''
  };

  get isCanadianTenantEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('canadianTenantEnabled', false);
  }

  get isHideSellAndPostFeesEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('posting-hideSellAndPostFeesEnabled', false);
  }

  get isBookValuationManheimValuationEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('bookValuation-manheimValuationEnabled', false);
  }

  get isFrenchCanadian(): boolean {
    const frenchLanguage = this.translocoService.getActiveLang().includes('fr');
    return frenchLanguage && this.isCanadianTenantEnabled;
  }

  get openingPrice() {
    return this.priceFeesForm.get('openingPrice');
  }

  get buyNowPrice() {
    return this.priceFeesForm.get('buyNowPrice');
  }

  get remainingDaysString() {
    return this.remainingDaysForm.get('remainingDays');
  }

  get openingPriceVal() {
    return parseInt(_get(this.openingPrice, 'value', 0));
  }

  get remainingDaysVal() {
    const remainDay = parseInt(_get(this.remainingDaysString, 'value'));
    return Number.isNaN(remainDay) ? null : remainDay;
  }

  get buyNowPriceVal() {
    const buyPrice = parseInt(_get(this.buyNowPrice, 'value', ''));
    return Number.isNaN(buyPrice) ? null : buyPrice;
  }

  get isOpeningPriceHasVal() {
    return this.openingPriceVal && this.openingPrice?.touched;
  }

  get isValidDefaultOpeningPrice() {
    return this.openingPriceVal && this.openingPriceVal < this.defaultOpeningPrice;
  }

  get isInvalidInputOpeningPrice() {
    return (
      this.priceFeesForm?.touched &&
      (!this.openingPrice?.value || this.isValidDefaultOpeningPrice || this.openingPrice?.errors?.pattern)
    );
  }

  get isInvalidRemainingDays() {
    return this.remainingDaysVal === 0 || this.remainingDaysVal === null;
  }

  get isBuyNowValueIsInvalid() {
    return this.openingPriceVal && this.buyNowPriceVal && this.buyNowPriceVal < this.openingPriceVal;
  }

  get bookProviderName() {
    return this.bookProviderService.bookProviderName;
  }

  get vehicleTitle() {
    return `${this.inventoryItem.itemDetail.modelYear || ''} ${this.inventoryItem.itemDetail.make || ''} ${this
      .inventoryItem.itemDetail.model || ''} ${this.inventoryItem.itemDetail.trim || ''}`;
  }

  get buyNowAllowed(): boolean {
    return this.postingData.inventoryItem.vehicleTypes.find(vh => vh.selected)?.buyNowAllowed;
  }

  get isNotNewPost(): boolean {
    return this.postingType !== PostingTypes.NEW;
  }

  get buyNowAndOpeningPriceNotEditable(): boolean {
    this.vehicleOwnerCode = this.postingData.inventoryItem.postingInformation.vehicleOwnerCode;
    return (
      this.vehicleOwnerCode && this.vehicleOwnerCode.toUpperCase() === 'ALLY' && this.isAdmin && !this.isPriceAdmin
    );
  }

  constructor(
    private readonly store: Store,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly inventoryService: InventoryService,
    private readonly bookProviderService: BookProviderService,
    private readonly bookValueHelperService: BookValueHelperService,
    private readonly auctionService: AuctionService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly translocoService: TranslocoService,
    private readonly postingFormTrackingService: PostingFormTrackingService,
    private readonly editPostingUtilsService: EditPostingUtilsService,
    private readonly launchDarklyService: LaunchDarklyService,
    private readonly postingAnalyticsHelperService: PostingAnalyticsHelperService,
    private readonly currencyFormatService: CurrencyFormatService
  ) {
    super();
  }

  ngOnInit() {
    this.getTranslations();
    this.setDefaultBookValue();
    this.store
      .select(EditPostingState.updatedPosting)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(postingData => {
        this.postingData = cloneDeep(postingData);
      });

    // Patch seller comment formControl when paidComment changes
    this.store
    .select(EditPostingState.updatedPosting)
    .pipe(
      map(postingData => postingData?.inventoryItem?.postingInformation?.paidComment),
      distinctUntilChanged(),
      takeUntil(this.destroyed$)
    )
    .subscribe(paidComment => {
      this.patchSellerCommentsIfNotDirty(paidComment);
    });

    this.postingType = this.store.selectSnapshot(EditPostingState.postingType);
    if (this.postingType === PostingTypes.NEW) {
      this.getBookValues();
      this.getPricingFeesFromOrganizationState();
    } else {
      this.getAuctionItem(this.postingData.inventoryItemUuid);
      this.getInventoryItem();
    }
    this.priceFeesForm = this.formBuilder.group({
      openingPrice: [_get(this.postingData, 'inventoryItem.valuation.openingPrice', ''), [openingPriceCustomValidator]],
      buyNowPrice: [_get(this.postingData, 'inventoryItem.valuation.buyNowPrice', '')?.toString()],
      sellerComments: [_get(this.postingData, 'inventoryItem.postingInformation.paidComment', null)]
    });

    this.remainingDaysForm = this.formBuilder.group({
      remainingDays: [_get(this.postingData, 'inventoryItem.postingInformation.remainingDays', null)]
    });

    this.assignPricingFeesForDisplay();
    this.getVehiclePostingConfig();
    this.translocoService.selectTranslateObject('UIComponents').subscribe(translatedObj => {
      this.bookValueTrans = translatedObj.bookValueTabs;
      this.characterCountData = translatedObj.characterCount;
    });

    //Called for form tracking on reload
    this.postingFormTrackingService.syncFormToInvalidValidationFormsOnLoad(
      this.postingType,
      EditPostingFormNames.PRICING_PRICING_AND_FEES
    );
    this.sendPageloadAnalyticsEvent();
    if (this.buyNowAndOpeningPriceNotEditable) {
      this.priceFeesForm.get('buyNowPrice').disable();
      this.priceFeesForm.get('openingPrice').disable();
    }
  }

  private patchSellerCommentsIfNotDirty(paidComment: string | undefined): void {
    const sellerCommentsControl = this.priceFeesForm?.get('sellerComments');
    if (
      sellerCommentsControl &&
      !sellerCommentsControl.dirty &&
      paidComment !== sellerCommentsControl.value
    ) {
      // If paidComment undefined, pass as form control default which is null
      sellerCommentsControl.patchValue(paidComment ?? null);
    }
 }

  sendPageloadAnalyticsEvent() {
    this.postingAnalyticsHelperService.sendPageloadAnalyticsEvent(
      this.store.selectSnapshot(ConfigurationState.configuration).tenantName,
      this.postingTypeString,
      EditPostingFormNames.PRICING_PRICING_AND_FEES
    );
  }

  /**
   * Retrieves organization fees from Org state and processes pricing fees for display
   */
  getPricingFeesFromOrganizationState() {
    const fees: OrganizationFees = this.store.selectSnapshot(OrganizationState.organizationFees);
    const feeTypes: FeeType[] = fees?.feeTypes;

    let transformedFees: Fee[];
    if (feeTypes?.length > 0) {
      transformedFees = feeTypes.map(feeType => this.buildFeeFromFeeType(feeType));
    }

    if (transformedFees?.length > 0) {
      this.setPostingFeesOnEditPostingState(transformedFees);
    }
  }

  setPostingFeesOnEditPostingState(fees: Fee[]) {
    _set(this.postingData, 'inventoryItem.valuation.fees', fees);
    this.store.dispatch(new UpdatePosting(this.postingData));
  }

  buildFeeFromFeeType(feeType: FeeType): Fee {
    return {
      amount: feeType?.feeAmount,
      code: this.mapFeeTypeToFeeCode(feeType?.feeType),
      description: feeType?.feeType
    } as Fee;
  }

  /**
   * Maps a fee type description to its fee code
   */
  mapFeeTypeToFeeCode(feeType: string): string {
    const feeDescriptionsToCodes = new Map<string, string>([
      [FeeTypes.SALES_FEE, FeeCodes.SALES_FEE],
      [FeeTypes.POSTING_FEE, FeeCodes.POSTING_FEE],
      [FeeTypes.AUCTION_COMMENT_FEE, FeeCodes.AUCTION_COMMENT_FEE]
    ]);
    return feeDescriptionsToCodes.get(feeType);
  }

  /**
   * assign field values to its respective variable from posting data
   */
  assignPricingFeesForDisplay() {
    const valuationFeesArr: Fee[] = _get(this.postingData, 'inventoryItem.valuation.fees', []);
    this.saleFormattedFee = formatCurrency(_get(_find(valuationFeesArr, { code: FeeCodes.SALES_FEE }), 'amount', 0));
    this.postingFormattedFee = formatCurrency(
      _get(_find(valuationFeesArr, { code: FeeCodes.POSTING_FEE }), 'amount', 0)
    );
    this.auctionCmtFormattedFee = formatCurrency(
      _get(_find(valuationFeesArr, { code: FeeCodes.AUCTION_COMMENT_FEE }), 'amount', 0)
    );
    this.remainingDays = _get(this.postingData, 'inventoryItem.postingInformation.remainingDays', 0);
    this.hitCount = _get(this.postingData, 'inventoryItem.hitCount', 0);
  }

  getVehiclePostingConfig() {
    this.inventoryService
      .getInventoryVehiclePostingConfig()
      .pipe(take(1))
      .subscribe(response => {
        this.vehiclePostingConfig = response;
        this.defaultOpeningPrice = parseFloat(
          _get(_find(this.vehiclePostingConfig, { key: 'PRICE_MINIMUM' }), 'value', '0')
        );
        this.defaultFormattedOpeningPrice = formatCurrency(this.defaultOpeningPrice);
      });
  }

  public trackPriceFeesFormChanges() {
    _set(this.postingData, 'inventoryItem.valuation.openingPrice', this.openingPriceVal);
    _set(this.postingData, 'inventoryItem.valuation.basePrice', this.openingPriceVal);
    _set(
      this.postingData,
      'inventoryItem.valuation.buyNowPrice',
      this.buyNowPriceVal == 0 ? null : this.buyNowPriceVal
    );
    _set(
      this.postingData,
      'inventoryItem.postingInformation.paidComment',
      _get(this.priceFeesForm.get('sellerComments'), 'value', null)
    );
    this.editPostingUtilsService.addToValidationForms(
      this.priceFeesForm.valid && this.openingPriceVal !== 0,
      EditPostingFormNames.PRICING_PRICING_AND_FEES
    );
    this.store.dispatch(new UpdatePosting(this.postingData));
  }

  trackRemainingDaysFormChanges() {
    _set(this.postingData, 'inventoryItem.postingInformation.remainingDays', this.remainingDaysVal);
    this.editPostingUtilsService.addToValidationForms(
      !this.isInvalidRemainingDays,
      EditPostingFormNames.PRICING_PRICING_AND_FEES
    );
    this.store.dispatch(new UpdatePosting(this.postingData));
  }

  restrictFormCharacters() {
    const formFieldValue = this.priceFeesForm.get('sellerComments').value;
    const sellerComments = formFieldValue?.replace(/[~";^|]/g, '');
    this.priceFeesForm.patchValue({
      sellerComments: sellerComments?.trim().length > 0 ? sellerComments : null
    });
  }

  getInventoryItem() {
    // Pipe to delay to prevent "Value changed after check" errors
    this.store
      .dispatch(
        new GetInventoryItem(
          this.postingData.inventoryItemUuid,
          false,
          this.editPostingUtilsService.getSelectedDealershipUuid()
        )
      )
      .pipe(
        delay(0),
        map(() => this.store.selectSnapshot(BuyVdpState.inventoryItem)),
        take(1)
      )
      .subscribe(inventoryItem => {
        this.inventoryItem = inventoryItem;
      });
  }

  setDefaultBookValue() {
    this.store
      .select(state => state.user.userProfile.userPreferences.defaultBookProvider.providerName)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(bookProviderName => {
        if (bookProviderName === 'NADA') {
          this.bookValue = 1;
        }
        if (bookProviderName && bookProviderName === 'Manheim Market Report') {
          this.bookValue = 2;
        }
      });
  }

  getAuctionItem(inventoryItemUuid?: string) {
    this.showLoader = true;
    let type, vinOrInventoryItemUuid;

    if (inventoryItemUuid) {
      type = 'INVENTORY_ITEM';
      vinOrInventoryItemUuid = inventoryItemUuid;
    } else {
      vinOrInventoryItemUuid = this.route.snapshot.params['vinOrInventoryItemUuid'];
      type = this.determineIfVinOrInventoryItemUuid(vinOrInventoryItemUuid);
    }

    this.auctionService
      .getAuctionItem(vinOrInventoryItemUuid, type)
      .pipe(take(1))
      .subscribe(
        auctionItem => this.updateAuctionItem(auctionItem),
        error => this.router.navigate(['/404'])
      );
  }

  updateAuctionItem(auctionItem: AuctionItem) {
    this.auctionItem = auctionItem;
    this.getBookValues();
  }

  getBookValues() {
    const bookValueRecalculationIsNeeded = this.store.selectSnapshot(EditPostingState.bookValueRecalculationNeeded);
    const storedBookValues = this.store.selectSnapshot(EditPostingState.displayedBookValues);

    if (!bookValueRecalculationIsNeeded && storedBookValues) {
      // Set book values from store if user returns after recalculation but with no other changes
      this.bookValueRecalculationInProgress = true;
      this.setRecalculatedTableAndModalValues(storedBookValues);
      this.bookValueRecalculationInProgress = false;
    } else if (bookValueRecalculationIsNeeded) {
      this.fetchAndSetCalculatedBookValues();
    } else if (this.auctionItem) {
      if (this.auctionItem.bookValues?.length > 0) {
        this.constructBookValueDisplayFromAuctionItemBookValues();
      } else {
        this.fetchAndSetCalculatedBookValues();
      }
    }
    
    this.bookValueHelperService.formatBookValues(
      this.formattedBlackBookValues,
      this.formattedNadaValues,
      this.formattedManheimValues,
      this.blackBookValues,
      this.nadaValues,
      this.manheimValues
    );
  }

  private fetchAndSetCalculatedBookValues(): void {
    if (this.requiredValuesForRecalculationExist()) {
      this.bookValueRecalculationInProgress = true;
      const calculateBookValuesRequest = this.buildCalculateBookValuesRequest(this.auctionItem, this.postingData);
      this.inventoryService.calculateBookValues(calculateBookValuesRequest).pipe(takeUntil(this.destroyed$)).subscribe({
        next: (response) => {
          this.store.dispatch(new SetDisplayedBookValues(response));
          this.setRecalculatedTableAndModalValues(response);
          this.bookValueRecalculationInProgress = false;
          this.store.dispatch(new SetBookValueRecalculationNotNeeded());
        },
        error: (error) => {
          console.error('Error while calling IIS to calculate book values.', error);
          this.bookValueRecalculationInProgress = false;
        }
      });
    }
  }

  get showMoreDetails() {
    const odometer = this.postingData?.inventoryItem?.vehicleInformation?.odometer;
    return odometer && odometer?.toString().length > 0;
  }

  requiredValuesForRecalculationExist(): boolean {
    if (
      this.isNotNullOrUndefined(this.postingData) &&
      this.isNotNullOrUndefined(this.postingData.inventoryItem) &&
      this.isNotNullOrUndefined(this.postingData.inventoryItem.vehicleInformation) &&
      this.isNotNullOrUndefined(this.postingData.inventoryItem?.storageLocation?.address) &&
      this.isNotNullOrUndefined(this.postingData.inventoryItem.vehicleInformation)
    ) {
      return (
        this.isNotNullOrUndefined(this.postingData.inventoryItem.vehicleInformation.vin) &&
        this.isNotNullOrUndefined(this.postingData.inventoryItem?.storageLocation?.address?.state) &&
        this.isNotNullOrUndefined(this.postingData.inventoryItem.vehicleInformation.vehicleDescription) &&
        this.isNotNullOrUndefined(this.postingData.inventoryItem.vehicleOptions)
      );
    } else {
      return false;
    }
  }

  isNotNullOrUndefined(value: any): boolean {
    return value !== null && value !== undefined;
  }

  // This logic is used to transform a CalculatedBookValuesDto into the models used for display on the Pricing & Fees Book Values table and modal.
  // i.e. Used after a book values recalculation response from IIS
  setRecalculatedTableAndModalValues(calculatedBookValuesDto: CalculatedBookValuesDto): void {
    this.blackBookValues = this.bookValueHelperService.transformRecalculatedBlackBookValuesForBookValueTable(
      calculatedBookValuesDto.blackBook
    );
    this.nadaValues = this.bookValueHelperService.transformRecalculatedJdPowerValuesForBookValueTable(
      calculatedBookValuesDto.jdPower
    );
    this.manheimValues = this.bookValueHelperService.transformRecalculatedManheimValuesForBookValueTable(
      calculatedBookValuesDto.manheim
    );
    this.recalculatedOrganizedModalData = this.bookValueHelperService.buildDataForBookValueDetailModal(
      calculatedBookValuesDto
    );
    this.bookValueHelperService.formatBookValues(
      this.formattedBlackBookValues,
      this.formattedNadaValues,
      this.formattedManheimValues,
      this.blackBookValues,
      this.nadaValues,
      this.manheimValues
    );
  }

  buildCalculateBookValuesRequest(
    auctionItem: AuctionItem,
    postingData: PostingDataResponseModel
  ): CalculateBookValuesRequest {
    const sellerOeUuid = this.store.selectSnapshot(EditPostingState.originalPosting).sellerOeUuid;
    return {
      auctionItemId: auctionItem?.legacyAuctionItemId,
      inventoryItemUuid: postingData?.inventoryItemUuid,
      vin: postingData?.inventoryItem?.vehicleInformation?.vin,
      providerName: null,
      valuationStateCode: postingData?.inventoryItem?.storageLocation?.address?.state,
      vehicleDescription: postingData?.inventoryItem?.vehicleInformation?.vehicleDescription,
      vehicleTrim: this.findCurrentTrimName(postingData),
      manufacturerModelCode: postingData?.inventoryItem?.vehicleInformation?.manufacturerModelCode,
      odometer: postingData?.inventoryItem?.vehicleInformation?.odometer,
      vehicleOptions: postingData?.inventoryItem?.vehicleOptions,
      dealerUuid: this.editPostingUtilsService.getSelectedDealershipUuid(sellerOeUuid)
    } as CalculateBookValuesRequest;
  }

  findCurrentTrimName(postingData: PostingDataResponseModel): string {
    const trimOptions = postingData?.inventoryItem?.vehicleOptions?.trimOptions;

    if (trimOptions && trimOptions.length > 0) {
      const trim = trimOptions.find(option => option.selected === true);
      if (trim) {
        return trim.vehicleTrimName;
      } else {
        return undefined;
      }
    }
    return undefined;
  }

  constructBookValueDisplayFromAuctionItemBookValues() {
    let blackBookValuesRaw;
    let nadaValuesRaw;
    let manheimValuesRaw;

    this.auctionItem.bookValues.forEach(item => {
      if (item.providerName === BookValueSpreadProviderName.BLACK_BOOK) {
        blackBookValuesRaw = item.values;
      }
      if (
        item.providerName === BookValueSpreadProviderName.NADA ||
        item.providerName === BookValueSpreadProviderName.JD_POWER
      ) {
        nadaValuesRaw = item.values;
      }
      if (
        item.providerName === BookValueSpreadProviderName.MANHEIM ||
        item.providerName === BookValueSpreadProviderName.MMR
      ) {
        manheimValuesRaw = item.values;
      }
    });
    if (blackBookValuesRaw) {
      this.blackBookValues = blackBookValuesRaw.reduce((accumalator, currentValue) => {
        return this.providerValue(currentValue, accumalator);
      }, {});
    }
    if (nadaValuesRaw) {
      this.nadaValues = nadaValuesRaw.reduce((accumalator, currentValue) => {
        return this.providerValue(currentValue, accumalator);
      }, {});
    }
    if (manheimValuesRaw) {
      this.manheimValues = manheimValuesRaw.reduce((accumalator, currentValue) => {
        return this.providerValue(currentValue, accumalator);
      }, {});
    }
  }

  private providerValue(currentValue: any, accumalator: any) {
    const newKey = currentValue.providerValue.replace(/\s+/g, '');
    accumalator[newKey] = {
      ...currentValue
    };
    return accumalator;
  }

  determineIfVinOrInventoryItemUuid(input: string) {
    if (input && input.length < 32) {
      return 'VIN';
    } else {
      return 'INVENTORY_ITEM';
    }
  }

  openBookValueModal(event) {
    this.bookValuesModalComponent.show(event);
    this.postingAnalyticsHelperService.trackModalView('bookValuesShowMoreDetails');
  }

  feeFormatCurrency(fee: string): string {
    const feeToNumber = parseFloat(fee.substring(1)); //substring function removes the dollar symbol.
    return this.currencyFormatService.formatCurrency(feeToNumber); //accepts string without dollar symbol.
  }

  getTranslations() {
    this.translocoService
      .selectTranslateObject('sellModule.posting.pricingAndFeesForm')
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translate => {
        this.errorMsg.multiplesPatternErr = translate.multiplesPatternErr;
        this.errorMsg.openingPrice.reqErr = translate.openingPriceReqErr;
        this.errorMsg.openingPrice.defaultOpeningPriceErr = translate.defaultOpeningPriceErr;
        this.errorMsg.buyNowPrice.exceedsOpeningPriceErr = translate.exceedsOpeningPriceErr;
        this.bookValueHelperService.formatBookValues(
          this.formattedBlackBookValues,
          this.formattedNadaValues,
          this.formattedManheimValues,
          this.blackBookValues,
          this.nadaValues,
          this.manheimValues
        );
      });
  }

  get isAdmin(): boolean {
    return this.editPostingUtilsService.isAdmin;
  }

  get isRemainingDaysEditable(): boolean {
    return this.isAdmin && this.postingType === PostingTypes.EDIT;
  }

  get isPriceAdmin(): boolean {
    return this.editPostingUtilsService.isPriceAdmin;
  }
}
